/**
 * DeviceGate — the blocking session surface, for BOTH shells.
 *
 * It renders for exactly three states, and it is the only thing on screen for
 * all of them:
 *
 *   limit       all sessions are in use. Pick one to end. Locked decision 3:
 *               the server NEVER picks, so this is not a notification — it is
 *               the decision itself. Two different causes land here and the
 *               copy differs (see `LimitCause`): a fresh 4th SIGN-IN, and being
 *               INTERRUPTED mid-session because your slot went away.
 *   evicted     this device was signed out from another device.
 *   signed_out  the token stopped being valid (401 from anywhere).
 *
 * ── WHY IT IS MOUNTED AT THE ROOT AND NOT INSIDE A SCREEN ─────────────────
 * The desktop shell has no auth gate at all. A session revoked server-side kept
 * rendering ALREADY-FETCHED prices and supplier contact details until the user
 * happened to navigate — data fetched legitimately, then the authorization that
 * justified it went away and nothing noticed (CLAUDE.md invariant 3, through
 * the back door). This overlay IS that gate: it covers the viewport, traps
 * focus, and blocks scroll, so a revoked session cannot keep reading what is
 * behind it. One mount point in main.tsx covers desktop and the mobile shell,
 * because they are one React tree.
 *
 * ── AND THEREFORE: THE EXITS ARE NOT OPTIONAL ─────────────────────────────
 * An overlay that covers the viewport, traps focus and blocks scroll is one
 * missing button away from being a lockout. A 401 classifier already shipped
 * exactly this shape once — the only control signed the user out, they signed
 * back in, repeated the action and hit the same wall. So EVERY state here
 * renders <ExitLadder/>, which carries a support mailto that works even when
 * the 409 body is empty or malformed, and a "Not now" that leaves. If you add a
 * fourth state and it does not render the ladder, it is not finished.
 *
 * ── THE TONE ──────────────────────────────────────────────────────────────
 * This is a paying trade buyer being interrupted mid-session, so it is built to
 * feel like a considered part of the product rather than an error dialog: a
 * slow ambient wash, a spring-settled card, and sessions laid out on a
 * log-time rail so the dormant one is visibly dormant. No red, no warning
 * triangle, no "Error". The one destructive act is named plainly, is the
 * user's, and is pre-selected so the common case is a single keystroke.
 *
 * ── MATERIAL / PERF ───────────────────────────────────────────────────────
 * The card used to be `.lqd-glass` sitting on a scrim that is ALREADY
 * `backdrop-blur-[20px]` — two stacked full-viewport backdrop filters plus
 * `lqd-drift`'s 8s loop, on the one screen a user is blocked on and the screen
 * most likely to be met on a cheap Android. It is now near-solid paper with the
 * `.lqd-edge` spectral rim (a masked gradient, not a filter, so it costs
 * nothing) and the blur stays on the scrim only. Nothing is lost visually —
 * the card sits on an already-blurred field.
 *
 * Motion budget is honest about `prefers-reduced-motion`: MotionConfig
 * (main.tsx) sets `reducedMotion="user"`, which collapses the transforms, and
 * the ambient drift + check-draw + rail draw are additionally gated on
 * `useReducedMotion()` so nothing loops for a user who asked for stillness.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  claimDevice, clearDeviceGate, getDeviceGateState, listDevices, revokeOtherSessions,
  setLimitBusy, subscribeDeviceGate,
  type DeviceGateState, type DeviceSummary,
} from '../lib/deviceSession'
import { getDeviceId } from '../lib/deviceId'
import { signOut } from '../lib/firebase'
import { useIdentity } from '../lib/identity'
import { feedback } from '../lib/feedback'
import { PressScale } from '../lib/PressScale'
import { isAppMode } from '../mobile/lib/appMode'
import { SessionRail } from './devices/SessionRail'
import { ExitLadder } from './devices/ExitLadder'
import { SharedLoginNote } from './devices/SharedLoginNote'
import { evictionCount, recordEviction, suggestedEvictionId } from './devices/deviceCopy'

const EASE_OUT = [0.16, 1, 0.3, 1] as const
const SPRING = { type: 'spring' as const, stiffness: 340, damping: 34 }

/* ─────────────────────────────── the shell ─────────────────────────────── */

function Scrim({
  children, still,
}: {
  children: React.ReactNode
  still: boolean
}) {
  return (
    <motion.div
      // `w-screen` + `h-[100dvh]`, NOT `inset-0`: the mobile shell sets
      // `html.app-mode { scrollbar-gutter: stable }`, which narrows the initial
      // containing block, and a `fixed inset-0` overlay then stops short of the
      // right edge — a cream seam down the side of a full-bleed sheet.
      // `h-[100dvh]` rather than `inset-0`'s implied height so iOS Safari's
      // collapsing toolbar can never crop the CTA off the bottom of the sheet.
      className="fixed inset-x-0 top-0 z-[999] h-[100dvh] flex items-end justify-center sm:items-center px-0 sm:px-6 pb-0 sm:pb-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
      role="dialog" aria-modal="true"
      aria-labelledby="device-gate-title" aria-describedby="device-gate-desc"
      data-testid="device-gate"
    >
      <div className="device-gate-scrim absolute inset-0 bg-navy-900/60 backdrop-blur-[20px]" />
      {/* Ambient silk — two slow washes, the same register as the app's own
          backdrop. Decorative, so it is skipped entirely under reduced motion. */}
      {!still && (
        <motion.div
          aria-hidden className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(58% 48% at 18% 12%, rgba(120,175,255,0.30), transparent 72%),'
              + 'radial-gradient(52% 44% at 86% 88%, rgba(201,169,74,0.24), transparent 72%)',
          }}
          animate={{ opacity: [0.45, 0.75, 0.45], scale: [1, 1.06, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      {children}
    </motion.div>
  )
}

function Card({
  children, still, cardRef,
}: { children: React.ReactNode; still: boolean; cardRef: React.RefObject<HTMLDivElement | null> }) {
  return (
    <motion.div
      ref={cardRef}
      // `tabIndex={-1}` is not decoration: it is the landing pad. Three of the
      // four states render no auto-focused control, and without a programmatic
      // target the overlay opened with focus still on whatever the user pressed
      // in the catalogue BEHIND it — so a screen reader never heard the dialog
      // and Tab kept walking the page it was supposed to be blocking.
      tabIndex={-1}
      className="lqd-edge relative w-full sm:max-w-[30rem] rounded-t-[22px] sm:rounded-lg bg-paper/[0.97]
                 shadow-elev-4 px-6 pt-7 pb-6 sm:px-8 sm:pt-9 sm:pb-8 max-h-[92dvh] overflow-y-auto
                 focus:outline-none"
      initial={still ? { opacity: 0 } : { opacity: 0, y: 26, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={still ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.99 }}
      transition={still ? { duration: 0.15 } : SPRING}
    >
      {/* Sheet grabber on the phone register only — the surface reads as a
          bottom sheet there and as a centered card from sm up. */}
      <div className="sm:hidden mx-auto mb-5 h-1 w-10 rounded-full bg-navy-900/15" aria-hidden />
      {children}
    </motion.div>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500/80">{children}</p>
  )
}

function Title({ children }: { children: React.ReactNode }) {
  return (
    <h2
      id="device-gate-title"
      className="mt-2 font-serif text-[24px] sm:text-[27px] leading-[1.15] tracking-h2 text-navy-900"
    >
      {children}
    </h2>
  )
}

/* ───────────────────────────── the limit state ─────────────────────────── */

const COPY = {
  signin: {
    eyebrow: 'Your account',
    title: (limit: number) => (
      // Non-breaking space so the count never orphans onto its own line at
      // 390px — "You’re signed in on 3" / "devices" reads like a bug.
      <>You’re signed in on {limit}&nbsp;devices</>
    ),
    body: (limit: number) =>
      `Diaex keeps ${limit} sessions per account. Choose one to sign out — this device takes its `
      + 'place, and you can sign back in on it any time.',
  },
  interrupted: {
    eyebrow: 'Session',
    title: () => <>This device needs a&nbsp;session</>,
    body: (limit: number) =>
      `All ${limit} of your sessions are in use elsewhere. Sign one out and you’ll carry straight `
      + 'on here — your cart, saved stones and orders are untouched.',
  },
} as const

function LimitBody({
  state, still, onNotNow, sharedLogin,
}: {
  state: Extract<DeviceGateState, { kind: 'limit' }>
  still: boolean
  onNotNow: () => void
  sharedLogin: boolean
}) {
  const others = useMemo(() => state.devices.filter((d) => !d.current), [state.devices])
  const suggested = useMemo(() => suggestedEvictionId(others), [others])
  // PRE-SELECTED. The screen used to demand a decision before it would let you
  // act: `selected` started null and the primary read "Choose a device to sign
  // out", disabled. The least-recently-used session is the answer ~always, so
  // it is armed on arrival and the whole interstitial becomes one keystroke.
  const [selected, setSelected] = useState<string | null>(suggested)
  useEffect(() => { setSelected((s) => (s && others.some((d) => d.deviceId === s) ? s : suggested)) }, [others, suggested])

  const chosen = others.find((d) => d.deviceId === selected) || null
  const identity = useIdentity()
  const copy = COPY[state.cause]

  const onContinue = useCallback(async () => {
    if (!chosen || state.busy) return
    setLimitBusy(true)
    const result = await claimDevice(chosen.deviceId)
    if (result === 'ok') return          // claimDevice clears the gate itself
    feedback.deny()
    setLimitBusy(false, result === 'limit'
      ? 'That session is still signed in. Pick another and try again.'
      : 'Couldn’t sign that session out. Check your connection and try again.')
  }, [chosen, state.busy])

  /** The recovery composition: nothing to pick, so re-ask rather than sit disabled. */
  const onRetry = useCallback(async () => {
    if (state.busy) return
    setLimitBusy(true)
    const result = await claimDevice()
    if (result === 'ok') return
    if (result === 'limit') return       // the fresh list is already on screen
    feedback.deny()
    setLimitBusy(false, 'Still can’t reach your sessions. Try again in a moment.')
  }, [state.busy])

  /**
   * THE LOST-DEVICE EXIT. Reachable from a device that holds no slot at all
   * (the devices router marks itself `deviceSelfService`), so a replacement
   * phone, a private window or cleared storage is recoverable without support.
   * If the 409 body arrived empty we go and fetch the list first, so this stays
   * available in exactly the state where the pick-one UI cannot help.
   */
  const onSignOutOthers = useCallback(async () => {
    if (state.busy) return
    setLimitBusy(true)
    let list: DeviceSummary[] = others
    if (list.length === 0) {
      const r = await listDevices()
      if (r.kind === 'ok') list = r.devices.filter((d) => !d.current)
    }
    const result = await revokeOtherSessions(list)
    if (result === 'ok') return
    feedback.deny()
    setLimitBusy(false, 'We couldn’t sign your other sessions out. Use “Trouble getting in?” below and we’ll clear them for you.')
  }, [others, state.busy])

  const empty = others.length === 0

  return (
    <>
      <Eyebrow>{copy.eyebrow}</Eyebrow>
      <Title>{copy.title(state.limit)}</Title>
      <p id="device-gate-desc" className="mt-3 text-sm leading-relaxed text-navy-500">
        {empty
          ? 'We couldn’t list your other sessions. You can try again, sign them all out, or ask us to clear them.'
          : copy.body(state.limit)}
      </p>

      {sharedLogin && <SharedLoginNote uid={identity.uid} deviceId={getDeviceId()} />}

      {!empty && (
        <div className="mt-6">
          <SessionRail
            devices={others}
            mode="choose"
            still={still}
            selectedId={selected}
            suggestedId={suggested}
            disabled={state.busy}
            onSelect={(id) => {
              setSelected(id)
              // `dim`, never `success`: choosing which of your own machines to
              // sign out is a loss, and success()'s two-note rise says it is a win.
              feedback.dim()
            }}
          />
        </div>
      )}

      {/* `role="status"` — a failed eviction was silent to a screen reader. */}
      <p role="status" aria-live="polite" className="mt-4 empty:mt-0 text-sm text-danger">
        {state.error}
      </p>

      <div className="mt-6 flex flex-col gap-2.5">
        <PressScale
          feel={false}
          className="btn-primary w-full"
          onClick={() => { void (empty ? onRetry() : onContinue()) }}
          disabled={state.busy || (!empty && !chosen)}
          data-testid="device-gate-cta"
        >
          {state.busy ? 'Signing out…' : empty ? 'Try again' : (
            // The device name is reinforcement, not information — the selected
            // row already carries the gold check — so it is dropped on the phone
            // register rather than wrapping a fixed-height pill onto two lines.
            <>Sign out<span className="hidden sm:inline">&nbsp;{chosen?.label}</span> and continue</>
          )}
        </PressScale>
        <ExitLadder
          devices={others}
          uid={identity.uid}
          deviceId={getDeviceId()}
          busy={state.busy}
          onSignOutOthers={() => { void onSignOutOthers() }}
          onNotNow={onNotNow}
        />
      </div>
    </>
  )
}

/* ────────────────────────── the message states ─────────────────────────── */

function MessageBody({
  eyebrow, title, body, cta, onCta, onNotNow, showSharedLogin,
}: {
  eyebrow: string
  title: string
  body: string
  cta: string
  onCta: () => void
  onNotNow: () => void
  showSharedLogin: boolean
}) {
  const identity = useIdentity()
  return (
    <>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Title>{title}</Title>
      <p id="device-gate-desc" className="mt-3 text-sm leading-relaxed text-navy-500">{body}</p>
      {showSharedLogin && <SharedLoginNote uid={identity.uid} deviceId={getDeviceId()} />}
      <div className="mt-7 flex flex-col gap-2.5">
        <PressScale feel={false} className="btn-primary w-full" onClick={onCta} data-testid="device-gate-cta">
          {cta}
        </PressScale>
        <ExitLadder
          devices={[]}
          uid={identity.uid}
          deviceId={getDeviceId()}
          busy={false}
          onNotNow={onNotNow}
        />
      </div>
    </>
  )
}

/* ──────────────────────────────── the gate ─────────────────────────────── */

export function DeviceGate() {
  const state = useSyncExternalStore(subscribeDeviceGate, getDeviceGateState, getDeviceGateState)
  const still = useReducedMotion() === true
  const open = state.kind !== 'idle'
  const restoreFocus = useRef<Element | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const evictionLogged = useRef(false)
  // In state, not a bare read: `recordEviction()` runs in an effect AFTER the
  // first paint, so a plain call during render would show the note one gate
  // too late — i.e. exactly never, for the user it is written for.
  const [evictions, setEvictions] = useState(() => evictionCount())

  // Count evictions so the shared-login note can fire. Once per transition INTO
  // the evicted state, never once per render.
  useEffect(() => {
    if (state.kind !== 'evicted') { evictionLogged.current = false; return }
    if (evictionLogged.current) return
    evictionLogged.current = true
    setEvictions(recordEviction())
  }, [state.kind])

  /**
   * WHAT TO GIVE FOCUS BACK TO, CAPTURED DURING RENDER — deliberately not in
   * the effect below. Child effects run BEFORE parent effects, so by the time
   * any effect here runs, <SessionRail/> has already focused its suggested
   * row: the effect would "restore" focus to a button inside the overlay that
   * is about to unmount, i.e. to nothing. The render pass is the last moment
   * at which `document.activeElement` is still the control the user pressed.
   */
  const wasOpen = useRef(false)
  if (open && !wasOpen.current) restoreFocus.current = document.activeElement
  wasOpen.current = open

  // Block the page behind it. An overlay that scrolls the catalogue underneath
  // is an overlay a revoked session can still read.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // The mobile shell sets `html.app-mode { scrollbar-gutter: stable }` to stop
    // the centred frame shifting between scrolling and non-scrolling screens.
    // While that gutter is reserved, Chrome excludes it from BOTH `100vw` and a
    // fixed element's containing block — so a full-bleed sheet stops ~15px
    // short and leaves a cream seam down its right edge. Nothing is scrolling
    // while this is open, so the gutter has no job: drop it and put it back.
    const html = document.documentElement
    const prevGutter = html.style.scrollbarGutter
    html.style.scrollbarGutter = 'auto'
    return () => {
      document.body.style.overflow = prev
      html.style.scrollbarGutter = prevGutter
      const back = restoreFocus.current
      restoreFocus.current = null
      // `isConnected`: the trigger may itself have been unmounted by whatever
      // ended the session, and focusing a detached node silently does nothing.
      if (back instanceof HTMLElement && back.isConnected) back.focus()
    }
  }, [open])

  /**
   * ── MOVE FOCUS IN. THE TRAP IS WORTHLESS WITHOUT THIS ───────────────────
   * Only ONE of the four states auto-focused anything: `limit` with a
   * non-empty list, where <SessionRail/> focuses the suggested row. The
   * `evicted`, `signed_out` and empty-list `limit` states left focus on
   * whatever the user last pressed in the catalogue — BEHIND a viewport-
   * covering, scroll-locked overlay. A screen reader therefore never heard the
   * dialog open (nothing announces a dialog nobody focused), and Tab kept
   * walking the page this component exists to block.
   *
   * The card itself is the landing pad rather than its first button: focusing
   * a control would skip the title and body, and the reason the session ended
   * is the entire point of the screen. `aria-labelledby`/`aria-describedby`
   * on the dialog make that one announcement.
   *
   * Child effects run before parent effects, so <SessionRail/> has already had
   * its say by the time this runs — the `contains` guard is what hands it the
   * priority, and it also stops a re-render from yanking focus off a control
   * the user has since tabbed to.
   */
  useEffect(() => {
    if (!open) return
    const card = cardRef.current
    if (!card) return
    if (card.contains(document.activeElement)) return
    card.focus({ preventScroll: true })
  }, [open, state.kind])

  /**
   * ── THE FOCUS TRAP THAT WAS DOCUMENTED BUT NEVER BUILT ──────────────────
   * The header comment has always claimed this overlay "traps focus"; it did
   * not. Tab from the last button walked straight into the catalogue BEHIND a
   * blocking overlay — the exact leak the component exists to close.
   *
   * It is bound on the DOCUMENT, in the capture phase, not as a React
   * `onKeyDown` on the scrim. A React handler only ever sees keys that bubble
   * from inside the dialog, so the trap engaged only once focus was already
   * where it belonged — i.e. it could restore a trap but never establish one.
   * With focus parked in the catalogue behind (which is exactly what used to
   * happen), Tab was never seen at all.
   *
   * It is built to FAIL OPEN: if the card somehow contains no focusable
   * element, Tab is left alone rather than swallowed, because a trap with
   * nothing to focus is a lockout. In practice <ExitLadder/> guarantees at
   * least two.
   *
   * Escape does NOT close (there is nothing safe to close to) — it moves focus
   * to the LAST control in the exit ladder. On a blocking screen Escape must
   * never be a silent no-op: that is precisely how a keyboard user concludes
   * they are stuck.
   */
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' && e.key !== 'Escape') return
      const card = cardRef.current
      if (!card) return
      // Deliberately NO visibility filter. Everything focusable inside this
      // card is on screen by construction, and an `offsetParent`/
      // `getClientRects` probe would be one layout quirk away from concluding
      // the card is empty and swallowing Tab — the lockout, not the fix.
      const nodes = Array.from(card.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ))
      if (nodes.length === 0) return       // fail open — never swallow the key
      if (e.key === 'Escape') {
        e.preventDefault()
        nodes[nodes.length - 1].focus()
        return
      }
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement
      // Outside the card entirely — a stray focus, or a browser that moved it
      // to <body> when a row unmounted. Either direction lands back inside.
      if (!card.contains(active)) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
        return
      }
      if (e.shiftKey && active === first) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open])

  const goSignIn = useCallback(() => {
    clearDeviceGate()
    void signOut().finally(() => {
      // A full navigation, not a router push: the tree behind this holds data
      // fetched under an authorization that no longer exists, and the only
      // honest way to drop ALL of it — component state, module caches, the
      // Zustand persist rehydration — is to reload.
      window.location.assign('/signin')
    })
  }, [])

  /**
   * Exit 4. Desktop can honestly promise anonymous browsing (locked prices,
   * invariant 3); the mobile shell cannot — its PUBLIC_PATHS stop at /welcome —
   * so it lands there instead, and the label in <ExitLadder/> says so.
   */
  const notNow = useCallback(() => {
    clearDeviceGate()
    const to = isAppMode() ? '/welcome' : '/search'
    void signOut().finally(() => { window.location.assign(to) })
  }, [])

  const shared = evictions >= 2

  return (
    <AnimatePresence>
      {open && (
        <Scrim key="device-gate" still={still}>
          <Card still={still} cardRef={cardRef}>
              {state.kind === 'limit' && (
                <LimitBody state={state} still={still} onNotNow={notNow} sharedLogin={shared} />
              )}
              {state.kind === 'evicted' && (
                <MessageBody
                  eyebrow="Session ended"
                  title="Signed out on this device"
                  body="This device was signed out from another one of your sessions. Sign in again to pick up where you left off — your cart, saved stones and orders are untouched."
                  cta="Sign in again"
                  onCta={goSignIn}
                  onNotNow={notNow}
                  showSharedLogin={shared}
                />
              )}
              {state.kind === 'signed_out' && (
                <MessageBody
                  eyebrow="Session ended"
                  title="Please sign in again"
                  body="Your session is no longer valid. Signing in again restores everything — nothing has been lost."
                  cta="Sign in"
                  onCta={goSignIn}
                  onNotNow={notNow}
                  showSharedLogin={false}
                />
              )}
          </Card>
        </Scrim>
      )}
    </AnimatePresence>
  )
}

export default DeviceGate
